/**
 * Brand palette of the GCC executive look (WI 2461, WI 2485). Shared so the
 * exported GCC dashboard (@tn/ui) and the GCC TN Analysis dashboards (@tn/core)
 * carry a client's brand the same way.
 *
 * Every brand-tinted surface (the hero, the sticky tab bar, the tab indicator,
 * soft highlights) is derived from the project's primary colour, so a client's
 * brand carries through the whole page instead of sitting on a fixed navy.
 *
 * To keep the page executive whatever the brand:
 * - the dark surfaces keep the brand's hue but are very dark (6–16% lightness)
 *   with capped saturation, so a neon brand reads as a deep tone, not a glow,
 *   and white text on them always has strong contrast;
 * - the tint used on dark surfaces and the ink used on light ones have their
 *   lightness fixed, so they stay legible for any brand lightness;
 * - with no accent colour, the second colour is a close analogous hue, which
 *   keeps the page tonal rather than multicolour.
 *
 * Chart series keep the fixed categorical palette in GCC_EXECUTIVE_TOKENS, so the
 * meaning of a series colour never changes with the brand.
 */

const DEFAULT_BRAND = '#A100FF'

interface Hsl { h: number; s: number; l: number }
type Rgb = [number, number, number]

/** A 3-, 4-, 6- or 8-digit hex colour as RGB (alpha ignored), or null. */
function parseHex(value: string | undefined): Rgb | null {
  const m = value?.trim().match(/^#([0-9a-f]{3,8})$/i)
  if (!m || m[1].length === 5 || m[1].length === 7) return null
  const hex = m[1].length <= 4 ? m[1].slice(0, 3).replace(/./g, c => c + c) : m[1].slice(0, 6)
  return [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16)) as Rgb
}

function toHsl([r, g, b]: Rgb): Hsl {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255]
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  const d = max - min
  if (d === 0) return { h: 0, s: 0, l }
  const s = d / (1 - Math.abs(2 * l - 1))
  const h = max === rn ? ((gn - bn) / d + 6) % 6 : max === gn ? (bn - rn) / d + 2 : (rn - gn) / d + 4
  return { h: h * 60, s, l }
}

function toRgb({ h, s, l }: Hsl): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const hp = (((h % 360) + 360) % 360) / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  const [r, g, b] = hp < 1 ? [c, x, 0] : hp < 2 ? [x, c, 0] : hp < 3 ? [0, c, x] : hp < 4 ? [0, x, c] : hp < 5 ? [x, 0, c] : [c, 0, x]
  const m = l - c / 2
  return [r, g, b].map(v => Math.round((v + m) * 255)) as Rgb
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))
const hex = (rgb: Rgb) => '#' + rgb.map(v => v.toString(16).padStart(2, '0')).join('').toUpperCase()
const rgba = (rgb: Rgb, a: number) => `rgba(${rgb.join(',')},${a})`

/** The CSS custom properties of the brand palette, keyed by property name. */
export function gccExecutivePalette(primary: string | undefined, accent?: string): Record<string, string> {
  const brandRgb = parseHex(primary) ?? (parseHex(DEFAULT_BRAND) as Rgb)
  const { h, s } = toHsl(brandRgb)
  // A near-grey brand gets a faint tint of its hue instead of a flat grey-black.
  const darkSat = clamp(s * 0.7, 0.18, 0.55)
  const night = toRgb({ h, s: darkSat, l: 0.06 })
  const accentRgb = parseHex(accent) ?? toRgb({ h: h + 28, s: clamp(s, 0.35, 0.7), l: 0.45 })
  return {
    '--brand': hex(brandRgb),
    '--brand2': hex(accentRgb),
    '--night': hex(night),
    '--night2': hex(toRgb({ h, s: darkSat, l: 0.1 })),
    '--night3': hex(toRgb({ h: h + 12, s: clamp(darkSat + 0.08, 0.2, 0.6), l: 0.16 })),
    '--nav-bg': rgba(night, 0.92),
    '--brand-on-dark': hex(toRgb({ h, s: clamp(s, 0.4, 0.85), l: 0.78 })),
    '--brand-ink': hex(toRgb({ h, s: clamp(s, 0.35, 0.8), l: 0.3 })),
    '--brand-soft': rgba(brandRgb, 0.09),
    '--brand2-soft': rgba(accentRgb, 0.06),
  }
}

/**
 * The palette as a CSS rule: `:root{...}` for the export's stylesheet, or
 * scoped to `selector` (the TN dashboard uses `html.tn-exec`). Every value is a
 * computed hex or rgba colour, so nothing from the branding reaches the CSS as typed.
 */
export function gccExecutivePaletteCss(primary: string | undefined, accent?: string, selector = ':root'): string {
  const vars = gccExecutivePalette(primary, accent)
  return `${selector}{${Object.entries(vars).map(([k, v]) => `${k}:${v}`).join(';')}}`
}
